import { Button, Chip, useDisclosure } from "@heroui/react";
import { useCallback, useEffect, useState } from "react";
import {
  FaList,
  FaPencil,
  FaPlus,
  FaRepeat,
  FaScaleBalanced,
} from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { api } from "@/api";
import type { AccountingIntegration } from "@/api/accounting/integrations";
import { CreateAccountingIntegrationModal } from "@/app/components/modals/accounting/createAccountingIntegrationModal";
import { EditAccountingIntegrationModal } from "@/app/components/modals/accounting/editAccountingIntegrationModal";
import { ListIntegrationChecksModal } from "@/app/components/modals/accounting/listIntegrationChecksModal";
import { TableBuilder } from "@/app/components/tables/tableBuilder";
import { ColumnType } from "@/app/components/tables/tableTypes";
import useErrorHandling from "@/hooks/useErrorHandling";
import {
  statusColors,
  statusLocale,
} from "@/app/components/modals/accounting/ui";

export function AccountingIntegrationsPage() {
  const [integrations, setIntegrations] = useState<
    AccountingIntegration[] | null
  >(null);
  const [integration, setIntegration] = useState<AccountingIntegration | null>(
    null,
  );

  const handleError = useErrorHandling();
  const navigate = useNavigate();

  const createModalDisclosure = useDisclosure();
  const editModalDisclosure = useDisclosure();
  const listChecksModalDisclosure = useDisclosure();

  const editIntegration = useCallback(
    (item: AccountingIntegration) => {
      setIntegration(item);
      editModalDisclosure.onOpen();
    },
    [editModalDisclosure],
  );

  const listChecks = useCallback(
    (item: AccountingIntegration) => {
      setIntegration(item);
      listChecksModalDisclosure.onOpen();
    },
    [listChecksModalDisclosure],
  );

  const checkIntegration = useCallback(
    (item: AccountingIntegration) => {
      api.accountingIntegrations
        .check(item.id)
        .then((result) => {
          if (result.message) {
            toast.success(result.message);
          } else {
            toast.error("Интеграция недоступна");
          }
        })
        .catch((err) => {
          const { errorMessage } = handleError(err);
          toast.error(errorMessage);
        });
    },
    [handleError],
  );

  const loadIntegrations = useCallback(() => {
    api.accountingIntegrations
      .list()
      .then((response) => {
        setIntegrations(response.data);
      })
      .catch((err) => {
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });
  }, [handleError]);

  useEffect(() => {
    loadIntegrations();
  }, [loadIntegrations]);

  useEffect(() => {
    if (!createModalDisclosure.isOpen && !editModalDisclosure.isOpen) {
      loadIntegrations();
    }
  }, [
    createModalDisclosure.isOpen,
    editModalDisclosure.isOpen,
    loadIntegrations,
  ]);

  return (
    <>
      <CreateAccountingIntegrationModal
        disclosure={createModalDisclosure}
        onCreated={loadIntegrations}
      />
      <EditAccountingIntegrationModal
        disclosure={editModalDisclosure}
        integration={integration}
        onUpdated={loadIntegrations}
      />
      <ListIntegrationChecksModal
        disclosure={listChecksModalDisclosure}
        integration={integration}
      />

      <div className="flex flex-row flex-wrap gap-4 w-full">
        <div className="flex-grow bg-white dark:bg-zinc-900 rounded-xl border-2 border-foreground-200 dark:border-foreground-100 p-4">
          <div className="flex flex-row justify-between w-full items-center mb-4">
            <div className="flex flex-row gap-4 items-center">
              <span className="font-medium">Интеграции</span>
            </div>
            <div className="flex flex-row gap-4 items-center">
              <Button
                startContent={<FaPlus />}
                color="primary"
                size="sm"
                variant="solid"
                className="max-w-fit"
                onPress={() => createModalDisclosure.onOpen()}>
                Создать
              </Button>
            </div>
          </div>
          <div className="flex flex-col gap-2 mb-2">
            <TableBuilder
              displayOptions={false}
              removeWrapper={true}
              columns={[
                {
                  key: "displayName",
                  label: "Название",
                  type: ColumnType.String,
                  sortable: true,
                },
                {
                  key: "instanceUrl",
                  label: "URL инстанса",
                  type: ColumnType.String,
                  sortable: true,
                },
                {
                  key: "username",
                  label: "Пользователь",
                  type: ColumnType.String,
                  sortable: true,
                },
                {
                  key: "enabled",
                  label: "Активна",
                  type: ColumnType.Boolean,
                  sortable: true,
                },
                {
                  key: "lastConnectionCheck",
                  label: "Последняя синхронизация",
                  type: ColumnType.Custom,
                  render: (
                    value: AccountingIntegration["lastConnectionCheck"],
                  ) => {
                    if (!value)
                      return <span className="text-default-400">Никогда</span>;

                    const time =
                      value.finishedAt || value.startedAt || value.createdAt;

                    return (
                      <div className="flex flex-col gap-1">
                        <Chip
                          size="md"
                          className="h-auto py-1 rounded-xl"
                          variant="bordered"
                          color={statusColors[value.status] || "default"}>
                          {statusLocale[value.status]}
                          <br />
                          {time && (
                            <span className="text-tiny text-default-400">
                              {new Date(time).toLocaleTimeString("ru-RU", {
                                timeZone: "Etc/Utc",
                              })}
                            </span>
                          )}
                        </Chip>
                      </div>
                    );
                  },
                },
                {
                  key: "actions",
                  label: "Действия",
                  type: ColumnType.Actions,
                  actions: [
                    {
                      icon: <FaRepeat />,
                      onClick: (_value, row) => {
                        checkIntegration(row);
                      },
                    },
                    {
                      icon: <FaList />,
                      onClick: (_value, row) => {
                        listChecks(row);
                      },
                    },
                    {
                      icon: <FaScaleBalanced />,
                      onClick: (_value, row) => {
                        navigate(
                          `/dashboard/accounting/integrations/${row.id}/conflicts`,
                        );
                      },
                    },
                    {
                      icon: <FaPencil />,
                      onClick: (_value, row) => {
                        editIntegration(row);
                      },
                    },
                  ],
                },
              ]}
              data={integrations || []}
              rowsPerPage={10}
              defaultSortDescriptor={{
                column: "displayName",
                direction: "ascending",
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}
